import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import { Box, ButtonDropdown, Grid } from '@hopskipdrive/react-components';
import { ActionBar, Button } from '@rideiq/shared/components';

import { analyticsCreateRideSeriesCancel, analyticsPage } from '../../../api/services/Analytics';
import { useAuthQuery } from '../../../auth/hooks/useAuthQuery';
import { defaultRideSeriesState, defaultSingleRideState } from '../../../constants';
import { useRideSubmit } from '../../../hooks/useRideSubmit';
import { RideCapRequestModal, RideConflictModal, RouteMap } from '../../../rides/components';
import { useNavigation } from '../../../shared/hooks/useNavigation';
import { dispatchFormSubmit } from '../../../utils/helpers';
import { trackFullStoryEvent } from '../../../vendor/features/FullStory/FullStory.service';
import { RidePage } from '../../components/RidePage';
import { RiderServiceRequestModal } from '../../components/RiderServiceRequestModal';
import { RideForm } from '../../features/RideForm';
import { useRideOverview } from '../../hooks/useRideOverview';

import { RideOverviewForAddRide } from './RideOverviewForAddRide';
import { GridContainerShadow, MapContainer } from './styles';

const AddRideContent: React.FC = () => {
  const { navigateTo } = useNavigation();

  useRideOverview();
  const formRef = useRef<HTMLFormElement>(null);
  const isSeries = !!useMatch('/rideseries/new');
  const [estimatedFare, setEstimatedFare] = useState<string>();
  const {
    formData,
    warningModal,
    setWarningModal,
    saveRide,
    setActionType,
    setReturnInfo,
    rideCapModalOpen,
    setRideCapModalOpen,
    riderServiceRequestModalOpen,
    setRiderServiceRequestModalOpen,
    riderListOfServices,
  } = useRideSubmit(isSeries, estimatedFare);
  const { user } = useAuthQuery();

  const location = useLocation();
  const { riderName, riderSlug } = location?.state || '';
  const [formValuesForSmartPooling, setFormValuesForSmartPooling] = useState<Partial<RideFormData>>();
  const openedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    const source = location?.state?.from?.match('/riders/') ? 'Rider Profile' : 'Rides';

    analyticsPage(isSeries ? 'Create Ride Series' : 'Create Ride', { source });
    trackFullStoryEvent('ride_create_started', { source });
  }, []);

  const handleEstimatedFareChange = (fare: string | undefined) => {
    setEstimatedFare(fare);
  };

  const handleRideCancel = () => {
    riderSlug ? navigateTo(`/riders/${riderSlug}`) : navigateTo('/rides');
    if (isSeries && user) {
      analyticsCreateRideSeriesCancel(user);
    }
    trackFullStoryEvent('ride_create_abandoned', {
      time_spent_seconds: Math.round((Date.now() - openedAtRef.current) / 1000),
    });
  };

  return (
    <Grid container direction="column">
      <Grid container direction="row" spacing={8}>
        <>
          <GridContainerShadow item xs={6} md={6} lg={6}>
            <ActionBar>
              <ActionBar.Title title={isSeries ? 'Create Ride Series' : 'Create Ride'} />
              <ActionBar.SideControls>
                <Box sx={{ paddingRight: 4 }} display="flex" gap="16px">
                  <Button color="tertiary" onClick={() => handleRideCancel()}>
                    Cancel
                  </Button>
                  <ButtonDropdown
                    ButtonComponent={Button}
                    data-testid="save-button-dropdown"
                    title="Save"
                    onClick={() => {
                      setReturnInfo({ riderName, riderSlug });
                      setActionType('save-ride', () => dispatchFormSubmit(formRef));
                    }}
                  >
                    <ButtonDropdown.MenuItem
                      icon={<ButtonDropdown.MenuItemIcon icon="check-circle-outlined" />}
                      onClick={() => {
                        setReturnInfo({ riderName, riderSlug });
                        setActionType('save-ride', () => dispatchFormSubmit(formRef));
                      }}
                    >
                      {'Save Ride'}
                    </ButtonDropdown.MenuItem>
                    <ButtonDropdown.MenuItem
                      icon={<ButtonDropdown.MenuItemIcon icon="duplicate" />}
                      onClick={() => {
                        setActionType('duplicate-ride', () => dispatchFormSubmit(formRef));
                      }}
                    >
                      {'Save and Duplicate'}
                    </ButtonDropdown.MenuItem>
                    <ButtonDropdown.MenuItem
                      icon={<ButtonDropdown.MenuItemIcon icon="repeat" />}
                      onClick={() => {
                        setActionType('reverse-duplicate-ride', () => dispatchFormSubmit(formRef));
                      }}
                    >
                      {'Save and Add Return Ride'}
                    </ButtonDropdown.MenuItem>
                  </ButtonDropdown>
                </Box>
              </ActionBar.SideControls>
            </ActionBar>
            <Grid sx={{ paddingRight: 4 }} item>
              <RideForm
                ref={formRef}
                onSubmit={saveRide}
                rideDetails={isSeries ? defaultRideSeriesState : defaultSingleRideState}
                isSeries={isSeries}
                onFormValues={setFormValuesForSmartPooling}
              />
            </Grid>
          </GridContainerShadow>
          <Grid item xs={6} md={6} lg={6} style={{ padding: 0 }}>
            <MapContainer data-testid="route-map">
              <RouteMap />
              <RideOverviewForAddRide
                isSeries={isSeries}
                formData={formValuesForSmartPooling}
                getEstimatedFare={handleEstimatedFareChange}
              />
            </MapContainer>
          </Grid>
        </>
      </Grid>
      <Box style={{ top: '10%', left: '10%', zIndex: 1000 }}>
        <RideCapRequestModal
          isOpen={rideCapModalOpen}
          handleResubmit={() => {
            saveRide({ ...formData, as_request: true });
            setRideCapModalOpen(false);
          }}
          handleClose={() => setRideCapModalOpen(false)}
        />
      </Box>
      <Box style={{ top: '20%', left: '20%', zIndex: 1100 }}>
        <RiderServiceRequestModal
          isOpen={riderServiceRequestModalOpen}
          listOfServices={riderListOfServices}
          handleResubmit={() => {
            saveRide({ ...formData, as_request: true });
            setRiderServiceRequestModalOpen(false);
          }}
          handleClose={() => setRiderServiceRequestModalOpen(false)}
        />
      </Box>
      <RideConflictModal
        isOpen={warningModal.isOpen}
        canResubmit={warningModal.canResubmit}
        message={warningModal.message}
        handleClose={() => setWarningModal({ ...warningModal, isOpen: false })}
        handleResubmit={() => {
          saveRide({ ...formData, skip_account_blocked_date: true });
          setWarningModal({ ...warningModal, isOpen: false });
        }}
      />
      <Outlet />
    </Grid>
  );
};

const AddRide = () => (
  <RidePage>
    <AddRideContent />
  </RidePage>
);

export default AddRide;
